<template>
  <div class="courses-slider" :class="[[$config.project]]">
    <div class="container-wrap container">
      <div
        v-for="(course, idx) in courses"
        :key="course.id"
        v-swipe.right="prevSlide"
        v-swipe.left="nextSlide"
        class="slide"
        :class="{ active: idx === active, [`slide-${idx}`]: true }"
      >
        <div class="slide-background" :style="{ backgroundImage: `url(${$webp(`${$config.imagesUrl}/courses/${course.bigImage}`)})` }" />
        <div class="slide-container col-5 md-6 offset-1 sm-offset-0">
          <p :title="course.name" class="course-name h3">
            {{ course.name }}
          </p>
          <div class="instructors">
            with
            <template
              v-for="(i, k) in course.instructorsArr"
            >
              <a v-if="$config.platform === 'ds'" :key="k" :href="`${$config.siteUrl}/instructors/${i.slug}`">{{ `${i.name}` }}</a>
              <span v-else :key="k">{{ `${i.name}` }}</span>
              {{ `${k !== (course.instructorsArr.length - 1) ? ', ' : ''}` }}
            </template>
          </div>
          <Rating v-if="course.reviews" :reviews="course.reviews" />
          <div class="info-container">
            <p class="body-1">
              {{ course.shortDescription }}
            </p>
            <Progress
              v-if="course.progress || course.started"
              :progress="course.progress"
              :data="course.lessons"
              :certificate="course.certificateNumber"
            />
            <div v-else class="info" :class="{ 'resize': $store.state.sideBarExpanded }">
              <div class="duration">
                {{ course.duration }} <span class="space">{{ course.duration === 1 ? 'Hour' : 'Hours' }}</span>
              </div>
              <div class="lessons">
                {{ course.lessons.total }} <span class="space">Lessons</span>
              </div>
              <div class="certificate">
                Certificate
              </div>
            </div>
          </div>
          <ButtonArrow
            v-if="course.certificateNumber"
            type="nuxt-link"
            :to="`/certificates/${course.certificateNumber}/`"
            class="continue-course"
            text="Course certificate"
          />
          <ButtonArrow
            v-else-if="course.progress === 100"
            :type="$store.state.layout === 'mobile' ? 'span' : 'nuxt-link'"
            :to="`/exams/${course.examId}/instructions/`"
            class="continue-course disabled"
            text="Course exam"
          />
          <ButtonArrow
            v-else-if="course.progress"
            type="nuxt-link"
            :to="`/courses/${course.slug}/`"
            class="continue-course"
            text="Continue watching"
          />
          <nuxt-link
            v-else
            :to="`${$config.project === 'site' ? '/signup/' : `/courses/${course.slug}/`}`"
            class="btn btn-primary start-course"
            data-event="start_course.click"
          >
            Start Course
          </nuxt-link>
          <div v-if="course.progress === 100" class="mobile-warning">
            <WarningIcon /> To attempt the exams please switch to a desktop device.
          </div>
        </div>
      </div>
      <div class="slider-navigation">
        <div
          v-for="(course, idx) in courses"
          :key="idx" class="dot"
          :class="{ active: active === idx }"
          @click="setActive(idx)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import Progress from './Progress';
import Rating from './Rating';
import ButtonArrow from '../components/ButtonArrow';
import WarningIcon from '../../src/assets/svg/icon-exam-warning.svg?vue-component';

export default {
  components: {
    Progress, Rating, ButtonArrow, WarningIcon,
  },
  props: {
    courses: { type: Array, required: true },
  },
  data: () => ({ active: 0 }),
  mounted() {
    const autoPlay = () => {
      if (this.active === this.courses.length - 1) {
        this.active = 0;
      } else {
        this.active += 1;
      }
    };
    let autoplay = setInterval(autoPlay, 15000);
    this.$el.querySelectorAll('.slider-navigation .dot').forEach((el) => {
      el.addEventListener('click', () => {
        clearInterval(autoplay);
        autoplay = setInterval(autoPlay, 15000);
      });
    });
    this.$once('hook:beforeDestroy', () => clearInterval(autoplay));
  },
  methods: {
    setActive(idx) {
      this.active = idx;
      clearInterval(this.autoplay);
    },
    prevSlide() {
      if (this.active === 0) {
        this.active = this.courses.length - 1;
      } else {
        this.active -= 1;
      }
    },
    nextSlide() {
      if (this.active === this.courses.length - 1) {
        this.active = 0;
      } else {
        this.active += 1;
      }
    },
  },
};
</script>

<style lang="scss">
.courses-slider {
  .container-wrap {
    position: relative;
    height: 467px;

    &:after {
      content: "";
      background: var(--main-dark);
      position: absolute;
      right: 0;
      left: 0;
      top: 0;
      bottom: 0;
      height: 100%;
      width: 100%;
      z-index: 0;
      border-radius: 10px;
    }

    .slide {
      padding: var(--spacing-70) 0;
      color: var(--main-light);
      border-radius: 10px;
      position: relative;
      height: 429px;

      &.slide-0,
      &.slide-1,
      &.slide-2 {
        position: absolute;
        right: 0;
        left: 0;
        top: 0;
        bottom: 0;
        height: 100%;
        width: 100%;
        z-index: 1;
        visibility: hidden;
        opacity: 0;
        transition: visibility 1s linear, opacity 1s linear;
        display: flex;
        align-items: center;
      }

      &.active {
        z-index: 2;
        visibility: visible;
        opacity: 1;
      }

      &:before {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 80px;
        background: linear-gradient(180deg, rgba(0, 12, 31, 0.6) 0%, rgba(0, 12, 31, 0) 100%);
        border-radius: 10px;
        pointer-events: none;
      }

      &:after {
        content: "";
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        height: 200px;
        background: linear-gradient(180deg, rgba(0, 12, 31, 0.6) 0%, rgba(0, 12, 31, 0) 100%);
        border-radius: 7px;
        transform: rotate(-180deg);
        pointer-events: none;
      }

      .slide-background {
        background-position: center;
        background-size: cover;
        background-repeat: no-repeat;
        image-rendering: -webkit-optimize-contrast;
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        width: 100%;
        height: 100%;
        border-radius: 10px;
        z-index: 0;
      }

      h3 {
        margin: 0;
        text-overflow: ellipsis;
        overflow: hidden;
        width: 100%;
        display: block;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
      }

      .course-name {
        margin-bottom: 0;
      }

      .instructors {
        margin: var(--spacing-8) 0;
        font-weight: 600;

        a {
          color: inherit;

          &:hover {
            color: var(--main-light-shade-4);
          }
        }
      }

      .info-container p {
        color: var(--main-light-shade-4);
        margin-top: var(--spacing-16);
        margin-bottom: var(--spacing-24);
        overflow: hidden;
        text-overflow: ellipsis;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 3;
        display: block;
        display: -webkit-box;
      }

      .slide-container {
        position: relative;
      }

      .mobile-warning {
        display: none;
      }

      .label-red {
        position: absolute;
        top: -32px;
      }

      .course-progress {
        margin-bottom: var(--spacing-28);

        .wrapper {
          margin-right: var(--spacing-8);
        }

        .progress-bar {
          background-color: var(--main-dark-light-3);
          border-color: var(--main-dark-light-4);
        }
      }

      .info {
        display: flex;
        align-items: center;
        margin-bottom: var(--spacing-48);

        > div {
          display: flex;
          align-items: center;
          margin-right: var(--spacing-30);
          position: relative;

          &:not(:last-child):after {
            content: "";
            width: 4px;
            height: 4px;
            border-radius: 50%;
            background: var(--main-dark-light-4);
            position: absolute;
            right: -18px;
          }

          &:last-of-type {
            margin-right: 0;
          }
        }

        .space {
          margin-left: var(--spacing-4);
        }

        &.resize {
          @media only screen and (max-width: 1600px) {
            .text {
              display: none;
            }

            .space {
              .uppercase {
                text-transform: uppercase;
              }
            }
          }
        }
      }

      .start-course,
      .continue-course {
        position: relative;
        z-index: 1;
        cursor: pointer;
        font-weight: 600;
        text-decoration: none;
      }

      .start-course {
        max-width: 300px;
        width: 100%;
        text-align: center;
        height: 58px;
        line-height: 58px;
        font-size: 18px;
        padding: 0;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .continue-course {
        height: 40px;
      }
    }

    .slider-navigation {
      z-index: 3;
      position: absolute;
      left: 50%;
      bottom: var(--spacing-16);
      transform: translateX(-50%);
      display: flex;

      .dot {
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background-color: rgba(255, 255, 255, 0.4);
        margin: 0 var(--spacing-8);

        &.active {
          background-color: var(--main-light);
        }
      }
    }
  }

  @include bp($bp-laptop) {
    .container-wrap .slide {
      .course-name {
        font-size: 28px;
      }

      .start-course {
        max-width: 225px;
        height: 40px;
      }

      .info-container p {
        margin-top: var(--spacing-28);
        margin-bottom: var(--spacing-12);
      }

      .info {
        margin-bottom: var(--spacing-28);

        > div {
          font-size: 14px;
        }
      }

      &:after {
        content: none;
      }
    }
  }

  @include bp($bp-tablet) {
    .container-wrap {
      height: 740px;

      .slide {
        .slide-background {
          background-size: 160%;
          background-position: 100% 0;
        }
      }
    }
  }

  @include bp($bp-mobile) {
    .container-wrap {
      &:after {
        border-radius: 0;
      }

      .slide {
        border-radius: 0;
        padding: 64px var(--spacing-24);
        background-size: 180%;
        align-items: flex-end !important;

        &:before {
          border-radius: 0;
          height: 100px;
          z-index: 1;
        }

        &:after {
          background: linear-gradient(180deg, var(--main-dark) 0%, rgba(0, 12, 31, 0.94) 0.01%, var(--main-dark) 61.47%, rgba(0, 12, 31, 0) 91.61%);
          border-radius: 0;
          height: 550px;
          bottom: 14px;
          z-index: -1;
        }

        p {
          -webkit-line-clamp: 4;
        }

        .info > div {
          font-size: 14px !important;
          margin-right: 34px;
          font-weight: 500;

          &:last-child {
            margin-right: 0;
          }

          svg {
            width: 20px;
          }
        }

        .slide-background {
          height: 450px;
          background-position: 77% 0;
          border-radius: 0;

          &:after {
            content: "";
            background: linear-gradient(180deg, var(--main-dark) 0%, rgba(0, 12, 31, 0) 100%);
            transform: rotate(-180deg);
            position: absolute;
            height: 150px;
            width: 100%;
            bottom: -2px;
          }
        }

        .slide-container {
          p {
            font-size: 16px;
            line-height: 24px;
          }
        }

        .course-progress {
          font-size: 14px;

          .progress-bar {
            width: 100px;
          }
        }

        .continue-course {
          color: var(--accent);
          border: 1px solid var(--accent);
          border-radius: 4px;
          text-align: center;
          height: 52px;
          min-width: 180px;
          width: 275px;
          padding: 0;
          justify-content: center;

          svg {
            display: none;
          }

          &:not(:disabled):hover {
            border-color: var(--accent);
            background-color: var(--accent);
            color: var(--main-light);
          }

          &:not(:disabled):active {
            border-color: var(--accent-shade-3);
            background-color: var(--accent-shade-3);
            color: var(--main-light);
          }
        }

        .start-course {
          height: 52px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
      }

      .slider-navigation {
        bottom: var(--spacing-32);
      }
    }

    &.site .container-wrap {
      .slide {
        padding-bottom: 124px;

        &:before {
          background: linear-gradient(180deg, var(--main-dark) 0%, rgba(0, 12, 31, 0.76) 37.5%, rgba(0, 12, 31, 0) 100%);
        }
      }
    }

    &.lms .container-wrap {
      .slide {
        padding-bottom: 63px;

        &:before {
          background: linear-gradient(180deg, rgba(0, 12, 31, 0.9) 0%, rgba(0, 12, 31, 0.94) 0.01%, rgba(0, 12, 31, 0) 100%);
        }
      }
    }
  }

  @include bp($bp-phone) {
    .container-wrap {
      .slide {
        background-size: 200%;
        background-position: 93% 0;

        .btn {
          width: 100%;
        }

        .continue-course {
          width: 100%;
          height: 52px;

          &.disabled {
            pointer-events: none;
            opacity: 0.3;
          }
        }

        .start-course {
          height: 52px;
          max-width: 100%;
        }
      }
    }

    .container-wrap .slide .mobile-warning {
      border: 1px solid var(--main-light-shade-4);
      border-radius: 10px;
      padding: var(--spacing-8);
      margin-top: var(--spacing-32);
      display: flex;

      svg {
        flex: 0 0 auto;
        margin-right: 10px;
        margin-top: 5px;

        > path:first-child {
          fill: var(--main-dark-light-4);
        }
      }
    }

    &.site .container-wrap {
      height: 913px;

      .slider-navigation {
        bottom: var(--spacing-80);
      }
    }

    &.lms .container-wrap {
      height: 748px;

      .slider-navigation {
        bottom: var(--spacing-32);
      }
    }
  }
}

/* Fading animation */
.fade {
  -webkit-animation-name: fade;
  -webkit-animation-duration: 4s;
  animation-name: fade;
  animation-duration: 4s;
}

@-webkit-keyframes fade {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes fade {
  from { opacity: 0; }
  to { opacity: 1; }
}

.fa {
  .courses-slider .container-wrap .slide .course-progress .progress-bar {
    background-color: var(--background-700);
    border-color: var(--main-dark-light-3);
  }
}

</style>
